<template>
  <div v-if="hasMedia" class="media-upload-area">
    <!-- Image preview grid -->
    <div v-if="images.length > 0" class="media-upload-area__images">
      <div
        v-for="(media, index) in images"
        :key="index"
        class="media-upload-area__image-item"
      >
        <img :src="media.url" :alt="`Attached image ${index + 1}`" />
        <button
          class="media-upload-area__remove"
          type="button"
          :aria-label="`Remove image ${index + 1}`"
          :disabled="disabled"
          @click="removeImage(index)"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <!-- Video preview -->
    <div v-if="video" class="media-upload-area__video">
      <div class="media-upload-area__video-item">
        <video
          :src="video.url"
          class="media-upload-area__video-player"
          muted
          @loadedmetadata="handleVideoLoaded"
        />
        <div class="media-upload-area__video-overlay">
          <v-icon size="48" color="white">mdi-play-circle-outline</v-icon>
        </div>
        <!-- Video info -->
        <div class="media-upload-area__video-info">
          <v-icon size="14">mdi-video</v-icon>
          <span>{{ video.file.name }}</span>
          <span v-if="videoDuration" class="media-upload-area__video-duration">
            {{ formatDuration(videoDuration) }}
          </span>
        </div>
        <button
          class="media-upload-area__remove"
          type="button"
          aria-label="Remove video"
          :disabled="disabled"
          @click="removeVideo"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <ActionFeedbackOverlay
      :show="feedback.show"
      :icon="feedback.icon"
      :text="feedback.text"
      :type="feedback.type"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import type { MediaPreview, VideoPreview } from "./types";

export type { MediaPreview, VideoPreview };

interface Props {
  images: MediaPreview[];
  video: VideoPreview | null;
  maxImages?: number;
  maxVideoSize?: number;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  maxImages: 4,
  maxVideoSize: 500 * 1024 * 1024, // 500MB
  disabled: false,
});

const emit = defineEmits<{
  "update:images": [images: MediaPreview[]];
  "update:video": [video: VideoPreview | null];
}>();

const { feedback, showFeedback } = useActionFeedback();

const videoDuration = ref(0);

const hasMedia = computed(() => props.images.length > 0 || props.video !== null);

const canAddImages = computed(() => {
  return props.images.length < props.maxImages && !props.video && !props.disabled;
});

const canAddVideo = computed(() => {
  return !props.video && props.images.length === 0 && !props.disabled;
});

function removeImage(index: number) {
  const newImages = [...props.images];
  const removed = newImages.splice(index, 1)[0];
  if (removed) {
    URL.revokeObjectURL(removed.url);
  }
  emit("update:images", newImages);
}

function removeVideo() {
  if (props.video) {
    URL.revokeObjectURL(props.video.url);
    emit("update:video", null);
    videoDuration.value = 0;
  }
}

function handleVideoLoaded(event: Event) {
  const videoEl = event.target as HTMLVideoElement;
  videoDuration.value = videoEl.duration;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Creates local previews only (uploads happen in background after submit)
function handleImageFiles(files: FileList | File[]) {
  const remainingSlots = props.maxImages - props.images.length;
  const newImages: MediaPreview[] = [];

  for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
    const file = files[i];
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      newImages.push({ url, file });
    }
  }

  if (newImages.length === 0) return;

  emit("update:images", [...props.images, ...newImages]);
}

// Creates local video preview only (upload happens in background after submit)
function handleVideoFile(file: File) {
  if (!file.type.startsWith("video/")) {
    showFeedback("mdi-alert-circle-outline", "Invalid file", "error");
    return;
  }

  if (file.size > props.maxVideoSize) {
    showFeedback("mdi-alert-circle-outline", "File too large", "error");
    return;
  }

  const url = URL.createObjectURL(file);
  emit("update:video", { url, file });
}

defineExpose({
  handleImageFiles,
  handleVideoFile,
  canAddImages,
  canAddVideo,
});
</script>

<style scoped lang="scss">
.media-upload-area {
  margin-top: 12px;

  &__images {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  &__image-item {
    position: relative;
    aspect-ratio: 16/9;
    border-radius: 12px;
    overflow: hidden;
    background: rgba(var(--v-theme-on-surface), 0.05);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--uploading img {
      opacity: 0.5;
    }
  }

  &__video {
    margin-top: 0;
  }

  &__video-item {
    position: relative;
    aspect-ratio: 16/9;
    border-radius: 12px;
    overflow: hidden;
    background: rgba(var(--v-theme-on-surface), 0.05);

    &--uploading .media-upload-area__video-player {
      opacity: 0.5;
    }
  }

  &__video-player {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__video-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    pointer-events: none;
  }

  &__video-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    color: white;
    font-size: 0.75rem;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__video-duration {
    margin-left: auto;
    opacity: 0.8;
    font-variant-numeric: tabular-nums;
  }

  &__progress {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    gap: 4px;
  }

  &__progress-text {
    font-size: 0.75rem;
    color: white;
    font-weight: 500;
  }

  &__complete {
    position: absolute;
    bottom: 8px;
    left: 8px;
    width: 24px;
    height: 24px;
    background: rgba(34, 197, 94, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 28px;
    height: 28px;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid #f59e0b;
      outline-offset: 2px;
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .media-upload-area {
    &__images {
      grid-template-columns: 1fr;
    }
  }
}
</style>
